/**
 * かな → 受理ローマ字表記テーブル。
 *
 * 初期データは Unity 版 `Assets/PaLTyping/Scripts/ChangeDictionary.cs` の移植。
 * そこから **標準的なIME(MS-IME / Google日本語入力)の受理表記** に拡張・修正している。
 * 各配列の 0 番目が「代表表記」だが、実際の表示は `automaton.ts` が
 * 「入力済みと整合する最短経路」で動的に決めるため、あくまでタイブレーク用の優先度。
 *
 * 促音「っ」と撥音「ん」は後続かなに依存するため、ここには**単独表記のみ**を置き、
 * 子音重ね / `n` 単独は `automaton.ts` が文脈から展開する。
 */

/** 促音。 */
export const SOKUON = 'っ';
/** 撥音。 */
export const HATSUON = 'ん';

/** 促音の単独表記(後続子音の重ねは automaton が生成する)。 */
export const SOKUON_SPELLINGS: readonly string[] = ['ltu', 'xtu', 'ltsu', 'xtsu'];

/**
 * 撥音の確定表記(`n` 単独は automaton が後続子音を見て生成する)。
 * `n'`(アポストロフィ)はJIS配列でShiftが必要で小学生には不向きなため採用しない。
 */
export const HATSUON_SPELLINGS: readonly string[] = ['nn', 'xn'];

/** テーブルに載るかなユニットの最大長(拗音の2かな)。 */
export const MAX_KANA_UNIT_LENGTH = 2;

const TABLE_SOURCE: Record<string, readonly string[]> = {
  // ── 清音 ────────────────────────────────────────────────
  あ: ['a'],
  い: ['i', 'yi'],
  う: ['u', 'wu', 'whu'],
  え: ['e'],
  お: ['o'],
  か: ['ka', 'ca'],
  き: ['ki'],
  く: ['ku', 'cu', 'qu'],
  け: ['ke'],
  こ: ['ko', 'co'],
  さ: ['sa'],
  し: ['si', 'shi', 'ci'],
  す: ['su'],
  せ: ['se', 'ce'],
  そ: ['so'],
  た: ['ta'],
  ち: ['ti', 'chi'],
  つ: ['tu', 'tsu'],
  て: ['te'],
  と: ['to'],
  な: ['na'],
  に: ['ni'],
  ぬ: ['nu'],
  ね: ['ne'],
  の: ['no'],
  は: ['ha'],
  ひ: ['hi'],
  ふ: ['hu', 'fu'],
  へ: ['he'],
  ほ: ['ho'],
  ま: ['ma'],
  み: ['mi'],
  む: ['mu'],
  め: ['me'],
  も: ['mo'],
  や: ['ya'],
  ゆ: ['yu'],
  よ: ['yo'],
  ら: ['ra'],
  り: ['ri'],
  る: ['ru'],
  れ: ['re'],
  ろ: ['ro'],
  わ: ['wa'],
  ゐ: ['wyi'],
  ゑ: ['wye'],
  を: ['wo'],

  // ── 濁音・半濁音 ────────────────────────────────────────
  が: ['ga'],
  ぎ: ['gi'],
  ぐ: ['gu'],
  げ: ['ge'],
  ご: ['go'],
  ざ: ['za'],
  じ: ['ji', 'zi'],
  ず: ['zu'],
  ぜ: ['ze'],
  ぞ: ['zo'],
  だ: ['da'],
  ぢ: ['di'],
  づ: ['du'],
  で: ['de'],
  ど: ['do'],
  ば: ['ba'],
  び: ['bi'],
  ぶ: ['bu'],
  べ: ['be'],
  ぼ: ['bo'],
  ぱ: ['pa'],
  ぴ: ['pi'],
  ぷ: ['pu'],
  ぺ: ['pe'],
  ぽ: ['po'],
  ゔ: ['vu'],

  // ── 小書き文字(x系 / l系)──────────────────────────────
  ぁ: ['la', 'xa'],
  ぃ: ['li', 'xi', 'lyi', 'xyi'],
  ぅ: ['lu', 'xu'],
  ぇ: ['le', 'xe'],
  ぉ: ['lo', 'xo'],
  ゃ: ['lya', 'xya'],
  ゅ: ['lyu', 'xyu'],
  ょ: ['lyo', 'xyo'],
  ゎ: ['lwa', 'xwa'],
  ゕ: ['lka', 'xka'],
  ゖ: ['lke', 'xke'],

  // ── 拗音(か行〜ぱ行)────────────────────────────────────
  きゃ: ['kya'],
  きぃ: ['kyi'],
  きゅ: ['kyu'],
  きぇ: ['kye'],
  きょ: ['kyo'],
  ぎゃ: ['gya'],
  ぎぃ: ['gyi'],
  ぎゅ: ['gyu'],
  ぎぇ: ['gye'],
  ぎょ: ['gyo'],
  しゃ: ['sya', 'sha'],
  しぃ: ['syi'],
  しゅ: ['syu', 'shu'],
  しぇ: ['sye', 'she'],
  しょ: ['syo', 'sho'],
  じゃ: ['ja', 'zya', 'jya'],
  じぃ: ['jyi', 'zyi'],
  じゅ: ['ju', 'zyu', 'jyu'],
  じぇ: ['je', 'zye', 'jye'],
  じょ: ['jo', 'zyo', 'jyo'],
  ちゃ: ['tya', 'cha', 'cya'],
  ちぃ: ['tyi', 'cyi'],
  ちゅ: ['tyu', 'chu', 'cyu'],
  ちぇ: ['tye', 'che', 'cye'],
  ちょ: ['tyo', 'cho', 'cyo'],
  ぢゃ: ['dya'],
  ぢぃ: ['dyi'],
  ぢゅ: ['dyu'],
  ぢぇ: ['dye'],
  ぢょ: ['dyo'],
  にゃ: ['nya'],
  にぃ: ['nyi'],
  にゅ: ['nyu'],
  にぇ: ['nye'],
  にょ: ['nyo'],
  ひゃ: ['hya'],
  ひぃ: ['hyi'],
  ひゅ: ['hyu'],
  ひぇ: ['hye'],
  ひょ: ['hyo'],
  びゃ: ['bya'],
  びぃ: ['byi'],
  びゅ: ['byu'],
  びぇ: ['bye'],
  びょ: ['byo'],
  ぴゃ: ['pya'],
  ぴぃ: ['pyi'],
  ぴゅ: ['pyu'],
  ぴぇ: ['pye'],
  ぴょ: ['pyo'],
  みゃ: ['mya'],
  みぃ: ['myi'],
  みゅ: ['myu'],
  みぇ: ['mye'],
  みょ: ['myo'],
  りゃ: ['rya'],
  りぃ: ['ryi'],
  りゅ: ['ryu'],
  りぇ: ['rye'],
  りょ: ['ryo'],

  // ── 外来音 ──────────────────────────────────────────────
  いぇ: ['ye'],
  うぁ: ['wha'],
  うぃ: ['wi', 'whi'],
  うぇ: ['we', 'whe'],
  うぉ: ['who'],
  ゔぁ: ['va'],
  ゔぃ: ['vi', 'vyi'],
  ゔぇ: ['ve', 'vye'],
  ゔぉ: ['vo'],
  ゔゃ: ['vya'],
  ゔゅ: ['vyu'],
  ゔょ: ['vyo'],
  くぁ: ['qa', 'qwa'],
  くぃ: ['qi', 'qyi', 'qwi'],
  くぅ: ['qwu'],
  くぇ: ['qe', 'qye', 'qwe'],
  くぉ: ['qo', 'qwo'],
  くゃ: ['qya'],
  くゅ: ['qyu'],
  くょ: ['qyo'],
  ぐぁ: ['gwa'],
  ぐぃ: ['gwi'],
  ぐぅ: ['gwu'],
  ぐぇ: ['gwe'],
  ぐぉ: ['gwo'],
  すぁ: ['swa'],
  すぃ: ['swi'],
  すぅ: ['swu'],
  すぇ: ['swe'],
  すぉ: ['swo'],
  つぁ: ['tsa'],
  つぃ: ['tsi'],
  つぇ: ['tse'],
  つぉ: ['tso'],
  てゃ: ['tha'],
  てぃ: ['thi'],
  てゅ: ['thu'],
  てぇ: ['the'],
  てょ: ['tho'],
  でゃ: ['dha'],
  でぃ: ['dhi'],
  でゅ: ['dhu'],
  でぇ: ['dhe'],
  でょ: ['dho'],
  とぁ: ['twa'],
  とぃ: ['twi'],
  とぅ: ['twu'],
  とぇ: ['twe'],
  とぉ: ['two'],
  どぁ: ['dwa'],
  どぃ: ['dwi'],
  どぅ: ['dwu'],
  どぇ: ['dwe'],
  どぉ: ['dwo'],
  ふぁ: ['fa', 'fwa'],
  ふぃ: ['fi', 'fyi', 'fwi'],
  ふぅ: ['fwu'],
  ふぇ: ['fe', 'fye', 'fwe'],
  ふぉ: ['fo', 'fwo'],
  ふゃ: ['fya'],
  ふゅ: ['fyu'],
  ふょ: ['fyo'],

  // ── 記号(全角→半角キー)────────────────────────────────
  '、': [','],
  '。': ['.'],
  '，': [','],
  '．': ['.'],
  '「': ['['],
  '」': [']'],
  ー: ['-'],
  '〜': ['~'],
  '～': ['~'],
  '！': ['!'],
  '？': ['?'],
  '・': ['/'],
  '　': [' '],
  '：': [':'],
  '；': [';'],
  '（': ['('],
  '）': [')'],
  '＝': ['='],
  '＋': ['+'],
  '＊': ['*'],
  '＆': ['&'],
  '＠': ['@'],
  '＃': ['#'],
  '％': ['%'],
  '＿': ['_'],
  '／': ['/'],
  '＼': ['\\'],
  '＜': ['<'],
  '＞': ['>'],
  '｛': ['{'],
  '｝': ['}'],
};

/** かな(1〜2文字)→ 受理ローマ字表記の一覧。 */
export const ROMAJI_TABLE: Readonly<Record<string, readonly string[]>> = TABLE_SOURCE;

/** 指定のかなユニットに対する受理表記を返す(無ければ undefined)。 */
export function getSpellings(kanaUnit: string): readonly string[] | undefined {
  return Object.prototype.hasOwnProperty.call(TABLE_SOURCE, kanaUnit)
    ? TABLE_SOURCE[kanaUnit]
    : undefined;
}

const KATAKANA_START = 0x30a1; // ァ
const KATAKANA_END = 0x30f6; // ヶ
const KANA_OFFSET = 0x60;

/** 全角英数字 → 半角。 */
const FULLWIDTH_ALNUM_START = 0xff01; // ！
const FULLWIDTH_ALNUM_END = 0xff5e; // ～

/**
 * 読み文字列を判定用に正規化する。
 *
 * - カタカナ → ひらがな(`ヴ` → `ゔ`)
 * - 全角スペース → 半角スペース
 * - 英大文字 → 小文字
 *
 * 全角記号はテーブル側で受理するのでここでは変換しない
 * (`ー` などカタカナ範囲外の記号を壊さないため)。
 */
export function normalizeReading(reading: string): string {
  let out = '';
  for (const ch of reading) {
    // for..of は必ず1文字以上を返すので charCodeAt(0) は undefined にならない。
    // サロゲートペアは上位サロゲートが下記の範囲外なので、そのまま else 節に落ちる。
    const code = ch.charCodeAt(0);
    if (code >= KATAKANA_START && code <= KATAKANA_END) {
      out += String.fromCodePoint(code - KANA_OFFSET);
    } else if (ch === '　') {
      out += ' ';
    } else if (ch >= 'A' && ch <= 'Z') {
      out += ch.toLowerCase();
    } else {
      out += ch;
    }
  }
  return out;
}

/** 全角英数記号を半角に変換する(DirectValidator 等の補助)。 */
export function toHalfWidth(text: string): string {
  let out = '';
  for (const ch of text) {
    const code = ch.charCodeAt(0);
    if (code >= FULLWIDTH_ALNUM_START && code <= FULLWIDTH_ALNUM_END) {
      out += String.fromCodePoint(code - 0xfee0);
    } else if (ch === '　') {
      out += ' ';
    } else {
      out += ch;
    }
  }
  return out;
}

const VOWELS = new Set(['a', 'i', 'u', 'e', 'o']);

/** 英小文字1文字か。 */
export function isLatinLetter(ch: string): boolean {
  return ch.length === 1 && ch >= 'a' && ch <= 'z';
}

/** 母音か。 */
export function isVowel(ch: string): boolean {
  return VOWELS.has(ch);
}

/**
 * 促音として重ねられる子音か。
 * 母音は不可。`n` も不可(`っん` は日本語に存在せず、`nn` と衝突するため)。
 */
export function isDoublableConsonant(ch: string): boolean {
  return isLatinLetter(ch) && !isVowel(ch) && ch !== 'n';
}

/**
 * 撥音を `n` 1文字で確定できる後続子音か。
 * 母音・`n`・`y` の前では `nn`(または `xn`)が必須(docs/02 §4)。
 */
export function isHatsuonTerminator(ch: string): boolean {
  return isLatinLetter(ch) && !isVowel(ch) && ch !== 'n' && ch !== 'y';
}
